import React, { useState, useEffect } from 'react';
import { Heading } from '../../components';
import { FaUser, FaEnvelope, FaPhoneAlt, FaCalendar, FaClock, FaUsers, FaComment } from 'react-icons/fa';
import axios from 'axios';

// API endpoint for submitting the booking form
const API_PATH = 'https://brewtopia-react.asdesignsgalaxy.com/api/phpmailer/booking.php';

// Regex patterns for email and phone number validation
const EMAIL_REGEX = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
const PHONE_REGEX = /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/;



const BookingForm = ({ formType }) => {

  // State to manage form data
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    number: '',
    seats: '',
    date: '',
    time: '',
    comment: ''
  });

  const [mailSent, setMailSent] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (mailSent) {
      const timer = setTimeout(() => setMailSent(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [mailSent]);

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const validationErrors = [];

      if (!formData.firstName.trim()) validationErrors.push('First name is required');
      if (!formData.lastName.trim()) validationErrors.push('Last name is required');
      if (!formData.email.trim()) {
        validationErrors.push('Email is required');
      } else if (!EMAIL_REGEX.test(formData.email)) {
        validationErrors.push('Invalid email format');
      }
      if (!formData.number.trim()) {
        validationErrors.push('Phone number is required');
      } else if (!PHONE_REGEX.test(formData.number)) {
        validationErrors.push('Invalid phone number format');
      }
      if (!formData.seats) validationErrors.push('Number of seats is required');
      if (!formData.date.trim()) validationErrors.push('Date is required');
      if (!formData.time.trim()) validationErrors.push('Time is required');
      if (formType === 'booking-page' && !formData.comment.trim()) validationErrors.push('Comment is required');

      if (validationErrors.length > 0) {
        throw new Error(validationErrors.join(' | '));
      }

      const postData = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        postData.append(key, value);
      });

      const response = await axios.post(API_PATH, postData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (!response.data.sent) {
        throw new Error(response.data.message || 'Reservation submission failed');
      }

      setMailSent(true);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        number: '',
        seats: '',
        date: '',
        time: '',
        comment: ''
      });
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Define reusable input field components as variables
  const inputFields = {
    firstName: (
      <div className="input-field">
        <label htmlFor="first-name"><FaUser /></label>
        <input type="text" name="firstName" className="box" id="first-name" 
          value={formData.firstName} onChange={handleInputChange}
          placeholder="First Name" disabled={isSubmitting} required />
      </div>
    ),
    lastName: (
      <div className="input-field">
        <label htmlFor="last-name"><FaUser /></label>
        <input type="text" name="lastName" className="box" id="last-name" 
          value={formData.lastName} onChange={handleInputChange}
          placeholder="Last Name" disabled={isSubmitting} required />
      </div>
    ),
    email: (
      <div className="input-field">
        <label htmlFor="email"><FaEnvelope /></label>
        <input type="email" name="email" className="box" id="email" 
          value={formData.email} onChange={handleInputChange}
          placeholder="Email" disabled={isSubmitting} required />
      </div>
    ),
    phone: (
      <div className="input-field">
        <label htmlFor="number"><FaPhoneAlt /></label>
        <input type="tel" name="number" className="box" id="number" 
          value={formData.number} onChange={handleInputChange}
          placeholder="Phone Number" disabled={isSubmitting} required />
      </div>
    ),
    seats: (
      <div className="input-field">
        <label htmlFor="seats"><FaUsers /></label>
        <input type="number" name="seats" className="box" id="seats" 
          value={formData.seats} onChange={handleInputChange}
          min="1" max="100" placeholder="Seats" disabled={isSubmitting} required />
      </div>
    ),
    date: (
      <div className="input-field">
        <label htmlFor="date"><FaCalendar /></label>
        <input type="text" name="date" placeholder="Date" id="date" className="box" 
          value={formData.date} onChange={handleInputChange}
          onFocus={(e) => (e.target.type = 'date')}
          onBlur={(e) => (e.target.type = 'text')} disabled={isSubmitting} required />
      </div>
    ),
    time: (
      <div className="input-field">
        <label htmlFor="time"><FaClock /></label>
        <input type="text" name="time" placeholder="Time" id="time" className="box" 
          value={formData.time} onChange={handleInputChange}
          onFocus={(e) => (e.target.type = 'time')}
          onBlur={(e) => (e.target.type = 'text')} disabled={isSubmitting} required />
      </div>
    ),
    comment: (
      <div className="input-field text-area">
        <label htmlFor="comment"><FaComment /></label>
        <textarea cols="30" rows="10" name="comment" className="box" id="comment" 
          value={formData.comment} onChange={handleInputChange}
          placeholder="Message" disabled={isSubmitting} required></textarea>
      </div>
    )
  };

  // Define reusable submit button component
  const submitButton = (
    <button type="submit" className="btn" name="submit" id="submit" disabled={isSubmitting}>
      {isSubmitting ? 'Submitting...' : 'Book Table'}
    </button>
  );

  // Render the form structure based on the `formType` prop
  const renderFormStructure = () => {
    if (formType === 'booking-page') {
      return (
        <>

          {/* Form heading */}
          <Heading title={'Online Reservation'} subTitle={'make a reservation'} sideHeading linear />

          <div className="input-box">
            {inputFields.firstName} {/* First Name Input */}
            {inputFields.lastName} {/* Last Name Input */}
          </div>

          <div className="input-box">
            {inputFields.phone} {/* Phone Number Input */}
            {inputFields.email} {/* Email Input */}
          </div>

          <div className="input-box">
            {inputFields.seats} {/* Seats No Input */}
          </div>

          <div className="input-box">
            {inputFields.date} {/* Pickup Date Input */}
            {inputFields.time} {/* Pickup Time Input */}
          </div>

          {inputFields.comment} {/* Instructions Text Area */}

          {submitButton} {/* Submit Button */}

        </>
      );
    } else if (formType === 'home-booking') {
      return (
        <>

          {/* Form heading */}
          <Heading title={'Book A Table'} subTitle={'make a reservation'} sideHeading linear />

          <div className="input-box">
            {inputFields.firstName} {/* First Name Input */}
            {inputFields.lastName} {/* Last Name Input */}
            {inputFields.phone} {/* Phone Number Input */}
            {inputFields.email} {/* Email Input */}
          </div>

          <div className="input-box">
            {inputFields.seats} {/* Seats No Input */}
            {inputFields.date} {/* Pickup Date Input */}
            {inputFields.time} {/* Pickup Time Input */}
            <div className='form-btn'>{submitButton}</div> {/* Submit Button */}
          </div>

        </>
      );
    }
  };

  return (
    <form className="dark-form" onSubmit={handleSubmitForm}>

      {renderFormStructure()}

      {error && (
        <div className="alert error">
          {error.split(' | ').map((msg, i) => (
            <span key={i}>{msg}</span>
          ))}
        </div>
      )}

      {mailSent && <div className="alert success"><span>Reservation submitted!</span></div>}

    </form>
  );

};

export default BookingForm;